import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AvatarPresetManager from "@/components/AvatarPresetManager";
import {
  Save, Globe, Mail, Phone, Bell, Instagram, Facebook, Youtube, Twitter, Linkedin,
  MessageCircle, Info, BarChart3, Radar, Plus, Trash2, PlayCircle,
} from "@/lib/icons";

const SOCIAL_META = {
  Instagram: { icon: Instagram, hint: "https://instagram.com/your-handle" },
  Facebook: { icon: Facebook, hint: "https://facebook.com/YourPage" },
  YouTube: { icon: Youtube, hint: "https://youtube.com/@YourChannel" },
  Twitter: { icon: Twitter, hint: "https://x.com/your-handle" },
  LinkedIn: { icon: Linkedin, hint: "https://linkedin.com/company/…" },
  WhatsApp: { icon: MessageCircle, hint: "https://wa.me/91XXXXXXXXXX" },
};

const inputCls = "h-11 bg-white/5 border-white/10 text-white placeholder:text-white/35";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/settings")
      .then((r) => setForm({
        website_name: r.data.website_name || "PRONET",
        contact_email: r.data.contact_email || "",
        contact_phone: r.data.contact_phone || "",
        admin_notification_email: r.data.admin_notification_email || "",
        social_links: (r.data.social_links || []).map((s) => ({ ...s, url: s.url || "", handle: s.handle || "" })),
        google_analytics_id: r.data.google_analytics_id || "",
        meta_pixel_id: r.data.meta_pixel_id || "",
        how_to_register_videos: r.data.how_to_register_videos?.length ? r.data.how_to_register_videos : [""],
      }))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setSocial = (i, field, val) => setForm((f) => {
    const list = f.social_links.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    return { ...f, social_links: list };
  });
  const setVideo = (i, val) => setForm((f) => {
    const list = f.how_to_register_videos.map((v, idx) => idx === i ? val : v);
    return { ...f, how_to_register_videos: list };
  });
  const addVideo = () => setForm((f) => ({ ...f, how_to_register_videos: [...f.how_to_register_videos, ""] }));
  const removeVideo = (i) => setForm((f) => ({ ...f, how_to_register_videos: f.how_to_register_videos.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, how_to_register_videos: form.how_to_register_videos.map((v) => v.trim()).filter(Boolean) };
      const { data } = await api.patch("/admin/settings", payload);
      setForm({
        website_name: data.website_name,
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        admin_notification_email: data.admin_notification_email || "",
        social_links: data.social_links || [],
        google_analytics_id: data.google_analytics_id || "",
        meta_pixel_id: data.meta_pixel_id || "",
        how_to_register_videos: data.how_to_register_videos?.length ? data.how_to_register_videos : [""],
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (loading || !form) return <div className="py-16 text-white/50">Loading settings…</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Workspace controls</div>
          <h1 className="mt-2 font-display text-5xl text-white">Settings</h1>
          <p className="admin-page-description">Manage integrations, public contact details, social links, registration guidance and reusable member avatars.</p>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={busy}
          className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Website */}
      <Panel title="Website" subtitle="Public identity of your PRONET installation.">
        <div className="grid md:grid-cols-2 gap-4">
          <FormRow label="Website name" icon={Globe}>
            <Input data-testid="settings-website-name" className={inputCls}
              value={form.website_name} onChange={setField("website_name")}
              placeholder="PRONET" />
          </FormRow>
          <FormRow label="Support phone" icon={Phone}>
            <Input data-testid="settings-contact-phone" className={inputCls}
              value={form.contact_phone} onChange={setField("contact_phone")}
              placeholder="+91-9999999999" />
          </FormRow>
          <FormRow label="Support email" icon={Mail}>
            <Input data-testid="settings-contact-email" className={inputCls}
              type="email" value={form.contact_email} onChange={setField("contact_email")}
              placeholder="contact@photographersnetwork.in" />
          </FormRow>
          <FormRow label="Registration notification email" icon={Bell}
            hint="Admin gets an email here when a new member registers.">
            <Input data-testid="settings-admin-notification-email" className={inputCls}
              type="email" value={form.admin_notification_email} onChange={setField("admin_notification_email")}
              placeholder="admin@yourdomain.com" />
          </FormRow>
        </div>
      </Panel>

      {/* Friend avatar presets */}
      <div id="avatars" className="scroll-mt-24">
        <AvatarPresetManager />
      </div>

      {/* Social */}
      <Panel title="Social media" subtitle="Placeholder icons in the footer become clickable once you add a URL. Leave blank to hide.">
        <div className="grid sm:grid-cols-2 gap-3">
          {form.social_links.map((s, i) => {
            const meta = SOCIAL_META[s.name] || {};
            const Icon = meta.icon || Globe;
            return (
              <div key={s.name} className="glass rounded-xl p-3 flex items-center gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/30 to-magenta-500/30 border border-white/10 text-fuchsia-200">
                  <Icon size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white">{s.name}</div>
                  <Input data-testid={`settings-social-${s.name.toLowerCase()}`}
                    className="mt-1 h-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                    value={s.url} onChange={(e) => setSocial(i, "url", e.target.value)}
                    placeholder={meta.hint || "https://…"} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Tracking & Analytics */}
      <Panel title="Tracking & analytics" subtitle="Paste in IDs from Google Analytics and Meta Events Manager to start collecting data. Leave blank to skip either one.">
        <div className="grid md:grid-cols-2 gap-4">
          <FormRow label="Google Analytics measurement ID" icon={BarChart3}
            hint="From analytics.google.com → Admin → Data Streams → your stream. Looks like G-XXXXXXXXXX.">
            <Input data-testid="settings-ga-id" className={inputCls}
              value={form.google_analytics_id} onChange={setField("google_analytics_id")}
              placeholder="G-XXXXXXXXXX" />
          </FormRow>
          <FormRow label="Meta Pixel ID" icon={Radar}
            hint="From business.facebook.com/events_manager → your Pixel → Settings. A numeric ID.">
            <Input data-testid="settings-meta-pixel-id" className={inputCls}
              value={form.meta_pixel_id} onChange={setField("meta_pixel_id")}
              placeholder="123456789012345" />
          </FormRow>
        </div>
      </Panel>

      {/* How to Register videos */}
      <Panel title="How to Register videos" subtitle="YouTube links shown on the public How to Register page, under the step-by-step guide. Leave a field blank to skip it.">
        <div className="space-y-3">
          {form.how_to_register_videos.map((url, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-fuchsia-200">
                <PlayCircle size={14} />
              </span>
              <Input data-testid={`settings-how-to-register-video-${i}`} className={inputCls}
                value={url} onChange={(e) => setVideo(i, e.target.value)}
                placeholder="https://youtube.com/watch?v=…" />
              <button type="button" onClick={() => removeVideo(i)} data-testid={`settings-how-to-register-video-remove-${i}`}
                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-500/20 text-red-300 hover:bg-red-500/10">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addVideo} data-testid="settings-how-to-register-video-add"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-white/70 hover:bg-white/5">
            <Plus size={13} /> Add video
          </button>
        </div>
      </Panel>

      <div className="glass rounded-2xl p-5 flex items-start gap-3">
        <Info size={16} className="text-fuchsia-300 mt-0.5 shrink-0" />
        <div className="text-sm text-white/60 leading-relaxed">
          Registration emails require a <span className="kbd">RESEND_API_KEY</span> in the backend environment.
          Until it's set, emails are <strong className="text-fuchsia-300">safely logged</strong> to the backend
          instead of being delivered. The rest of the flow (Resend button, notification recipient, saved settings) works today.
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="settings-save-bottom" onClick={save} disabled={busy}
          className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="admin-surface space-y-5 p-5 sm:p-6 fade-up">
      <div>
        <div className="admin-section-heading">{title}</div>
        {subtitle && <div className="admin-section-copy">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function FormRow({ label, icon: Icon, hint, children }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px] flex items-center gap-1">
        {Icon && <Icon size={11} className="text-fuchsia-300" />} {label}
      </label>
      <div className="mt-2">{children}</div>
      {hint && <div className="mt-1.5 text-xs text-white/40">{hint}</div>}
    </div>
  );
}
